/**
 * A page-wide budget on how many <video> elements may decode at once.
 *
 * WEB-2593 follow-up: even with lazy loading and off-screen pausing, a media
 * heavy page (the air landing page has ~90 <video> tags) can momentarily line
 * up more concurrent decoders than iOS Safari's small hardware pool allows —
 * which is what tips an older device (e.g. iPhone XS) past its per-tab memory
 * limit and gets the whole page killed/reloaded.
 *
 * This is a safety net, not the primary mechanism: acquire() when a video
 * starts playing and release() when it pauses. When the number of concurrent
 * players would exceed `limit`, the least-recently-acquired one is paused
 * (LRU). An evicted-but-still-visible video resumes on its next visibility /
 * shouldPlay change; it does not auto-resume when a slot frees, so keep the
 * desktop limit high enough that eviction only bites on constrained viewports.
 */
export const createVideoDecodeLimiter = (limit) => {
  // Ordered oldest -> newest by most recent acquire().
  const active = [];

  const evictDownTo = (max, exempt) => {
    while (active.length > max) {
      const oldest = active.shift();
      if (oldest && oldest !== exempt && typeof oldest.pause === "function") {
        oldest.pause();
      }
    }
  };

  return {
    acquire(videoEl) {
      if (!videoEl) return;
      const i = active.indexOf(videoEl);
      if (i !== -1) active.splice(i, 1);
      active.push(videoEl);
      evictDownTo(limit, videoEl);
    },
    release(videoEl) {
      const i = active.indexOf(videoEl);
      if (i !== -1) active.splice(i, 1);
    },
    setLimit(next) {
      limit = next;
      evictDownTo(limit);
    },
    size() {
      return active.length;
    },
  };
};

// Concurrent-decoder caps. Desktop's is a loose backstop that comfortably fits
// the airblivious marquee's visible slides; mobile is tight because that's
// where the hardware decoder pool is scarce.
const DESKTOP_LIMIT = 8;
const MOBILE_LIMIT = 2;
const MOBILE_BREAKPOINT = 769;

const initialLimit =
  typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT
    ? MOBILE_LIMIT
    : DESKTOP_LIMIT;

/**
 * Shared, page-wide limiter every VideoPlayer draws from. Its cap follows the
 * viewport so phones stay tight while desktops keep the lively marquee.
 */
export const videoDecodeLimiter = createVideoDecodeLimiter(initialLimit);

if (typeof window !== "undefined") {
  const syncLimit = () => {
    videoDecodeLimiter.setLimit(
      window.innerWidth < MOBILE_BREAKPOINT ? MOBILE_LIMIT : DESKTOP_LIMIT
    );
  };
  window.addEventListener("resize", syncLimit);
}
